import React, { useEffect, useState } from "react";
import AddBoxIcon from "@mui/icons-material/AddBox";
import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";
import Dialog from "@mui/material/Dialog";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import ChecklistIcon from "@mui/icons-material/Checklist";
import { PageHeader, PageShell } from "../../components/UI/Page";

// Dialogs, inputs and menus take their colors from the app-wide MUI theme
// (MuiThemeBridge), so no local overrides are needed here.

function UserChecklist() {
  const [checkboxSections, setCheckboxSections] = useState({
    Add: [],
    Edit: [],
    Remove: [],
  });
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openRemoveDialog, setOpenRemoveDialog] = useState(false);
  const [newFieldName, setNewFieldName] = useState("");
  const [selectedField, setSelectedField] = useState("");
  const [selectedSection, setSelectedSection] = useState("");

  const handleOpenAddDialog = (section) => {
    setOpenAddDialog(true);
    setSelectedSection(section);
    setNewFieldName("");
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
  };

  const handleAddField = (section) => {
    if (newFieldName.trim() !== "") {
      const updatedSections = { ...checkboxSections };
      updatedSections[section] = [
        ...updatedSections[section],
        { name: newFieldName, checked: false },
      ];
      setCheckboxSections(updatedSections);
      setOpenAddDialog(false);
    }
  };

  const handleOpenRemoveDialog = (section) => {
    setOpenRemoveDialog(true);
    setSelectedSection(section);
    setSelectedField("");
  };

  const handleCloseRemoveDialog = () => {
    setOpenRemoveDialog(false);
  };

  const handleRemoveField = (section) => {
    if (selectedField !== "") {
      const updatedSections = { ...checkboxSections };
      updatedSections[section] = updatedSections[section].filter(
        (field) => field.name !== selectedField
      );
      setCheckboxSections(updatedSections);
      setOpenRemoveDialog(false);
    }
  };

  const handleCheckboxChange = (fieldName, section) => {
    const updatedSections = { ...checkboxSections };
    updatedSections[section] = updatedSections[section].map((field) => {
      if (field.name === fieldName) {
        return { ...field, checked: !field.checked };
      }
      return field;
    });
    setCheckboxSections(updatedSections);
  };

  const renderFields = (section) => {
    if (!checkboxSections[section].length) {
      return (
        <p className="app-muted py-2 text-sm">
          No fields yet — use + to add one.
        </p>
      );
    }
    return checkboxSections[section].map((field, index) => (
      <label
        key={index}
        className="flex min-h-[2.5rem] cursor-pointer items-center gap-3 rounded-xl px-2 text-sm hover:bg-[color:var(--app-hover)]"
      >
        <input
          type="checkbox"
          className="h-4 w-4 accent-sky-600 dark:accent-sky-400"
          checked={field.checked}
          onChange={() => handleCheckboxChange(field.name, section)}
        />
        <span>{field.name}</span>
      </label>
    ));
  };

  const renderSection = (section) => (
    <section className="app-card flex flex-col rounded-2xl border p-5">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">{section}</h2>
        <div className="flex items-center gap-1">
          <Tooltip title="Add a field">
            <button
              type="button"
              className="app-icon-btn"
              aria-label={`Add a field to ${section}`}
              onClick={() => handleOpenAddDialog(section)}
            >
              <AddBoxIcon />
            </button>
          </Tooltip>
          <Tooltip title="Remove a field">
            <button
              type="button"
              className="app-icon-btn"
              aria-label={`Remove a field from ${section}`}
              onClick={() => handleOpenRemoveDialog(section)}
            >
              <IndeterminateCheckBoxIcon />
            </button>
          </Tooltip>
        </div>
      </div>
      <div className="space-y-0.5">{renderFields(section)}</div>
    </section>
  );

  const renderDropdownOptions = () => {
    const sectionFields = checkboxSections[selectedSection];
    if (!sectionFields) return null; // Guard against undefined section
    return sectionFields.map((field) => (
      <MenuItem key={field.name} value={field.name}>
        {field.name}
      </MenuItem>
    ));
  };

  return (
    <PageShell size="xl">
      <PageHeader
        icon={ChecklistIcon}
        tone="slate"
        eyebrow="Admin"
        title="Big C User Portal Checklist"
        description="Build Add, Edit, and Remove checklists and tick items off as you go. Checklists aren't saved — they reset when you leave the page."
      />

      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {/* Add Section */}
        {renderSection("Add")}
        {/* Edit Section */}
        {renderSection("Edit")}
        {/* Remove Section */}
        {renderSection("Remove")}
      </div>

      {/* Dialog for Adding Checkbox Field */}
      <Dialog
        open={openAddDialog}
        onClose={handleCloseAddDialog}
        fullWidth
        maxWidth="xs"
      >
        <div className="p-6">
          <TextField
            label="Add Checkbox Field"
            variant="outlined"
            fullWidth
            value={newFieldName}
            onChange={(e) => setNewFieldName(e.target.value)}
          />
          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              className="app-btn app-btn-ghost"
              onClick={handleCloseAddDialog}
            >
              Cancel
            </button>
            <button
              type="button"
              className="app-btn app-btn-primary"
              onClick={() => handleAddField(selectedSection)}
            >
              Add
            </button>
          </div>
        </div>
      </Dialog>
      {/* Dialog for Removing Checkbox Field */}
      <Dialog
        open={openRemoveDialog}
        onClose={handleCloseRemoveDialog}
        fullWidth
        maxWidth="xs"
      >
        <div className="p-6">
          <h2 className="mb-4 font-semibold">Select Field to Remove:</h2>
          <TextField
            select
            fullWidth
            label="Field"
            value={selectedField}
            onChange={(e) => setSelectedField(e.target.value)}
            variant="outlined"
          >
            {renderDropdownOptions()}
          </TextField>
          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              className="app-btn app-btn-ghost"
              onClick={handleCloseRemoveDialog}
            >
              Cancel
            </button>
            <button
              type="button"
              className="app-btn app-btn-danger"
              onClick={() => handleRemoveField(selectedSection)}
            >
              Remove
            </button>
          </div>
        </div>
      </Dialog>
    </PageShell>
  );
}

export default UserChecklist;
